import { Component, OnInit, OnDestroy, Input, EventEmitter, Output, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { InputForm } from 'ustore-ui';

import { ToastService } from '../../../shared/services/toast.service';
import { GetCardsData } from '../../../core/models/card.model';
import { CreditCardListComponent } from '../../../shared/components/credit-card-list/credit-card-list.component';
import { InputCreditCardNumberComponent } from '../../../shared/components/input-credit-card-number/input-credit-card-number.component';
import { expiryDateValidator } from '../../../shared/validators/expiry-date.validator';
import { cvcValidator } from '../../../shared/validators/cvc.validator';
import { CustomValidators } from '../../../shared/validators/custom-validators';
import { cardNameValidator } from '../../../shared/validators/card-name.validator';
import { ValidationMessagePipe } from '../../../shared/pipes/validation-message.pipe';
import { CnpjPipe } from '../../../shared/pipes/cnpj.pipe';
import { CreditCardService } from '../../../core/services/credit-card.service';
import { UserFacade } from '../../../core/store/user/user.facade';
import { ExternalCreditCardService } from '../../../core/services/external-credit-card.service';
import { ExternalCreditCardRequest } from '../../../core/models/external-credit-card.model';
import { DeviceInfoService } from '../../../core/services/device-info.service';
import { DeviceInfo } from '../../../core/models/device-info.model';
import { User } from '../../../core/models/user.model';
import { LoadingComponent } from '../../../shared/components/loading/loading.component';
import { I18nPipe } from '../../../shared/pipes/i18n.pipe';
import { I18nService } from '../../../core/services/i18n.service';
import { unformatCnpj } from '../../../core/utils/cnpj.util';

@Component({
  selector: 'umkt-plan-payment-card',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputForm,
    CreditCardListComponent,
    InputCreditCardNumberComponent,
    ValidationMessagePipe,
    LoadingComponent,
    I18nPipe
  ],
  templateUrl: './plan-payment-card.component.html',
  styleUrl: './plan-payment-card.component.scss',
})
export class PlanPaymentCardComponent implements OnInit, OnChanges, OnDestroy {
  private destroy$ = new Subject<void>();
  private langSubscription?: Subscription;

  creditCardForm: FormGroup;
  cnpjPipe = new CnpjPipe();
  showCardForm: boolean = false;
  cardsInfo: GetCardsData[] = [];
  selectedCardId: string | 'new' = 'new';
  userUuid: string | null = null;
  userData: User | null = null;
  deviceInfo: DeviceInfo | null = null;
  isLoading: boolean = false;

  @Input() currentCardToken: string | null = null;
  @Output() cardFormVisibilityChange = new EventEmitter<boolean>();
  @Output() tokenCardSelected = new EventEmitter<string>();
  private fb = inject(FormBuilder);
  private creditCardService = inject(CreditCardService);
  private externalCreditCardService = inject(ExternalCreditCardService);
  private userFacade = inject(UserFacade);
  private toastService = inject(ToastService);
  private deviceInfoService = inject(DeviceInfoService);
  private i18nService = inject(I18nService);

  constructor() {
    this.creditCardForm = this.fb.group({
      bandeira: [null, [Validators.required]],
      document: [null, [Validators.required, CustomValidators.cnpj]],
      cardHolder: [null, [Validators.required, CustomValidators.noWhitespaceOnly, cardNameValidator(this.i18nService)]],
      cardSecurityCode: [null, [Validators.required, cvcValidator(this.i18nService)]],
      cardNumber: [null, Validators.required],
      cardExpirationDate: [null, [Validators.required, expiryDateValidator(this.i18nService)]],
    });
  }

  ngOnInit(): void {
    this.captureDeviceInfo();

    this.langSubscription = this.i18nService.getCurrentLanguage().subscribe(() => {
      const cvcControl = this.creditCardForm.get('cardSecurityCode');
      if (cvcControl) {
        cvcControl.updateValueAndValidity({ emitEvent: false }); 
      }

      const holderControl = this.creditCardForm.get('cardHolder');
      if (holderControl) {
        holderControl.updateValueAndValidity({ emitEvent: false });
      }

      const expiryControl = this.creditCardForm.get('cardExpirationDate');
      if (expiryControl) {
        expiryControl.updateValueAndValidity({ emitEvent: false });
      }
    });

    this.userFacade.getLoginData().subscribe(loginData => {
      if (loginData && loginData.userUuid) {
        this.userUuid = loginData.userUuid;
        this.loadCards();
      }
    });

    this.userFacade.getUserDetails().subscribe(userData => {
      this.userData = userData;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentCardToken'] && this.currentCardToken) {
      this.selectedCardId = this.currentCardToken;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private captureDeviceInfo(): void {
    this.isLoading = true;
    this.deviceInfoService.getDeviceInfo().subscribe({
      next: deviceInfo => {
        this.deviceInfo = deviceInfo;
        this.isLoading = false;
      },
      error: error => {
        console.error('Erro ao capturar informações do dispositivo:', error);
        this.isLoading = false;
      },
    });
  }

  private loadCards(cardTokenToSelect?: string): void {
    this.isLoading = true;
    if (!this.userUuid) {
      this.isLoading = false;
      return;
    }

    this.creditCardService.getCards(this.userUuid).subscribe({
      next: response => {
        let cards = response.data ?? [];

        // Se não houver cartões, adicionar cartões mockados para teste
        if (cards.length === 0) {
          cards = [
            {
              id: 999991,
              cardToken: 'mock_visa_token_001',
              cardFlag: 'visa',
              lastFourDigits: '4242',
              userUuid: this.userUuid || '',
              walletId: 1,
              createdAt: new Date().toISOString()
            },
            {
              id: 999992,
              cardToken: 'mock_master_token_002',
              cardFlag: 'mastercard',
              lastFourDigits: '5555',
              userUuid: this.userUuid || '',
              walletId: 1,
              createdAt: new Date().toISOString()
            },
            {
              id: 999993,
              cardToken: 'mock_elo_token_003',
              cardFlag: 'elo',
              lastFourDigits: '6362',
              userUuid: this.userUuid || '',
              walletId: 1,
              createdAt: new Date().toISOString()
            }
          ];
        }

        cards.sort((a, b) => b.id - a.id);

        this.cardsInfo = cards;

        if (cardTokenToSelect && this.cardsInfo.some(card => card.cardToken === cardTokenToSelect)) {
          this.selectedCardId = cardTokenToSelect;
          this.onCreditCardSelected(this.selectedCardId);
        }

        this.isLoading = false;
      },
      error: error => {
        console.error('Erro ao buscar cartões:', error);
        this.isLoading = false;
      },
    });
  }

  onCreditCardSelected(value: string | 'new') {
    this.selectedCardId = value;
    if (value === 'new') {
      this.showCardForm = true;
    } else {
      this.showCardForm = false;
    }
    this.cardFormVisibilityChange.emit(this.showCardForm);
    this.tokenCardSelected.emit(this.selectedCardId);
  }

  onCancelCard() {
    this.selectedCardId = 'new';
    this.showCardForm = false;
    this.cardFormVisibilityChange.emit(this.showCardForm);
  }

  onCardFlagChange(cardFlag: string) {
    this.creditCardForm.get('bandeira')?.setValue(cardFlag);
  }

  onRegisterCard() {
    if (this.creditCardForm.valid && this.userData) {
      this.isLoading = true;
      const _cardRequest: ExternalCreditCardRequest = {
        userUuid: this.userData.uuid || '',
        cliente: {
          identificacaoCliente:
            this.cnpjPipe.removeCnpjMask(this.userData.document || '') || '',
          documento: this.cnpjPipe.removeCnpjMask(this.userData.document || ''),
          telefone: this.userData.phone || '',
          nome:
            this.userData.displayName ||
            `${this.userData.firstName} ${this.userData.lastName}`.trim(),
        },
        cartoes: [
          {
            bandeira: this.creditCardForm.value.bandeira || '',
            cardHolder: this.creditCardForm.value.cardHolder || '',
            cardNumber:
              this.creditCardForm.value.cardNumber.replace(/\D/g, '') || '',
            cardSecurityCode: this.creditCardForm.value.cardSecurityCode || '',
            cardExpirationDate:
              this.creditCardForm.value.cardExpirationDate || '',
            fraud: true,
            fatura: {
              customerIdentity:
                this.cnpjPipe.removeCnpjMask(this.userData.document || '') ||
                '',
              nome:
                this.userData.displayName ||
                `${this.userData.firstName} ${this.userData.lastName}`.trim(),
              endereco: this.userData.addresses[0].address || '',
              complemento: this.userData.addresses[0].complement || '',
              cidade: this.userData.addresses[0].city || '',
              estado: this.userData.addresses[0].state || '',
              codigoPostal: this.userData.addresses[0].zipCode || '',
              pais: 'BR',
              telefone: this.userData.phone || '',
              email: this.userData.email || '',
            },
            antifraude: {
              clienteIP: this.deviceInfo?.ip || null,
              device: this.deviceInfo?.deviceType || null,
              nomeComprador:
                this.userData.displayName ||
                `${this.userData.firstName} ${this.userData.lastName}`.trim(),
              documento:
                this.cnpjPipe.removeCnpjMask(this.userData.document || '') ||
                '',
              telefoneDDD: this.userData.phone.slice(0, 2),
              telefoneNumero: this.userData.phone.slice(2),
              moeda: 'BRL',
              email: this.userData.email || '',
              enderecoEntrega: {
                logradouro: this.userData.addresses[0].address || '',
                numero: this.userData.addresses[0].number || '',
                complemento: this.userData.addresses[0].complement || '',
                cidade: this.userData.addresses[0].city || '',
                uf: this.userData.addresses[0].state || '',
                cep: this.userData.addresses[0].zipCode || '',
                pais: 'BR',
                infoNumero: this.userData.addresses[0].number || '',
                infoComplemento: this.userData.addresses[0].complement || '',
              },
              merchantDefinedData: [
                {
                  mddFieldId: '1',
                  mddFieldValue: this.deviceInfo?.operatingSystem || 'Linux',
                },
                {
                  mddFieldId: '8',
                  mddFieldValue: 'Ustore',
                },
                {
                  mddFieldId: '11',
                  mddFieldValue:
                    this.userData.displayName ||
                    `${this.userData.firstName} ${this.userData.lastName}`,
                },
                {
                  mddFieldId: '12',
                  mddFieldValue: this.cnpjPipe.removeCnpjMask(
                    this.userData.document || ''
                  ),
                },
                {
                  mddFieldId: '17',
                  mddFieldValue: '1',
                },
              ],
            },
          },
        ],
      };
      this.externalCreditCardService.createCreditCard(_cardRequest).subscribe({
        next: (response) => {
          const newCardToken = response.token;

          this.creditCardForm.reset();
          this.showCardForm = false;
          this.toastService.showSuccess(
            this.i18nService.instant('pages.paymentCard.messages.success')
          );
          this.cardFormVisibilityChange.emit(this.showCardForm);
          this.loadCards(newCardToken);
        },
        error: _error => {
          this.isLoading = false;
          this.toastService.showError(
            this.i18nService.instant('pages.paymentCard.messages.error')
          );
        },
      });
    } else {
      this.markFormGroupTouched(this.creditCardForm);
      this.toastService.showError(
        this.i18nService.instant('pages.paymentCard.messages.formInvalid')
      );
    }
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  onExpiryDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    const numbers = value.replace(/\D/g, '');
    const limited = numbers.substring(0, 6);

    let formatted = '';
    if (limited.length <= 2) {
      formatted = limited;
    } else {
      formatted = limited.substring(0, 2) + '/' + limited.substring(2, 6);
    }

    input.value = formatted;
    this.creditCardForm
      .get('cardExpirationDate')
      ?.setValue(formatted, { emitEvent: false });
  }

  onCvcInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    const numbers = value.replace(/\D/g, '');
    const limited = numbers.substring(0, 4);

    input.value = limited;
    this.creditCardForm
      .get('cardSecurityCode')
      ?.setValue(limited, { emitEvent: false });
  }

  onCnpjInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    const cleanValue = unformatCnpj(value);
    const limitedValue = cleanValue.substring(0, 14);

    if (cleanValue.length > 14) {
      const formatted = this.cnpjPipe.transform(limitedValue);
      input.value = formatted;
      this.creditCardForm
        .get('document')
        ?.setValue(formatted, { emitEvent: true });
      return;
    }

    const formatted = this.cnpjPipe.transform(value);

    input.value = formatted;
    this.creditCardForm
      .get('document')
      ?.setValue(formatted, { emitEvent: true });
  }

  onCnpjPaste(event: ClipboardEvent): void {
    const pastedText = event.clipboardData?.getData('text') || '';
    const cleanValue = unformatCnpj(pastedText);

    if (cleanValue.length > 14) {
      event.preventDefault();
      const documentControl = this.creditCardForm.get('document');
      if (documentControl) {
        documentControl.setErrors({ invalidCNPJ: true });
        documentControl.markAsTouched();
      }
      return;
    }

    event.preventDefault();
    const formatted = this.cnpjPipe.transform(cleanValue);

    const input = event.target as HTMLInputElement;
    input.value = formatted;
    this.creditCardForm
      .get('document')
      ?.setValue(formatted, { emitEvent: true });
  }

  blockLeadingSpace(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;

    if (event.key === ' ' && input.selectionStart === 0) {
      event.preventDefault();
    }
  }

  onCardNameInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    const cleaned = value.replace(/[^a-zA-Z\s]/g, '');
    const upperCased = cleaned.toUpperCase();
    const limited = upperCased.substring(0, 64);

    if (input.value !== limited) {
      input.value = limited;
    }

    this.creditCardForm
      .get('cardHolder')
      ?.setValue(limited, { emitEvent: false });
  }
}

